import { useTranslation } from "react-i18next";
import { scorePasswordStrength, type PasswordStrengthLabel } from "@nexus/contracts";
import { cn } from "@nexus/ui";

const SEGMENT_COUNT = 4;

const LABEL_KEYS: Record<PasswordStrengthLabel, string> = {
  weak: "auth.passwordStrengthWeak",
  fair: "auth.passwordStrengthFair",
  good: "auth.passwordStrengthGood",
  strong: "auth.passwordStrengthStrong",
};

/** Heat ramp keyed by filled-segment count (1 = weakest). */
const BAR_COLOR: Record<number, string> = {
  1: "bg-[var(--error)]",
  2: "bg-[var(--warning)]",
  3: "bg-[var(--info)]",
  4: "bg-[var(--success)]",
};

const TEXT_COLOR: Record<number, string> = {
  1: "text-[var(--error)]",
  2: "text-[var(--warning)]",
  3: "text-[var(--info)]",
  4: "text-[var(--success)]",
};

/** Advisory password strength meter — purely informational, never blocks submit. */
export function PasswordStrengthMeter({ password }: { password: string }) {
  const { t } = useTranslation();
  if (password.length === 0) return null;

  const { score, label } = scorePasswordStrength(password);
  // A rule-failing password (score 0) still shows one red segment so "Weak"
  // isn't paired with an empty bar.
  const filled = Math.max(score, 1);

  return (
    <div className="space-y-1.5">
      <div className="flex gap-1" aria-hidden="true">
        {Array.from({ length: SEGMENT_COUNT }).map((_, index) => (
          <span
            key={index}
            className={cn(
              "h-1 flex-1 rounded-full transition-colors duration-300 ease-out motion-reduce:transition-none",
              index < filled ? BAR_COLOR[filled] : "bg-[var(--muted)]",
            )}
          />
        ))}
      </div>
      <p className="text-xs" aria-live="polite">
        <span className="text-[var(--muted-foreground)]">{t("auth.passwordStrengthLabel")}: </span>
        <span className={cn("font-medium", TEXT_COLOR[filled])}>{t(LABEL_KEYS[label])}</span>
      </p>
    </div>
  );
}
